import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { createPersistMiddleware, PERSIST_VERSION } from '../../../shared/stores/persistMigration';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: Date;
}

export interface Modal {
  id: string;
  isOpen: boolean;
  title: string;
  content: string | React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  size?: 'small' | 'medium' | 'large';
}

export interface UIState {
  // Estado de paneles
  panels: {
    ifcInput: boolean;
    cadasterInput: boolean;
    configuration: boolean;
    drawing: boolean;
    map: boolean;
    calculations: boolean;
    export: boolean;
  };
  
  // Modales
  modals: Modal[];
  activeModalId: string | null;
  
  // Notificaciones
  notifications: Notification[];
  
  // Configuración de UI
  theme: 'light' | 'dark';
  language: 'es' | 'en';
  sidebarCollapsed: boolean;
  
  // Estado de carga
  isLoading: boolean;
  loadingMessage: string;
  
  // Acciones de paneles
  togglePanel: (panelName: keyof UIState['panels']) => void;
  openPanel: (panelName: keyof UIState['panels']) => void;
  closePanel: (panelName: keyof UIState['panels']) => void;
  closeAllPanels: () => void;
  
  // Acciones de modales
  openModal: (modal: Omit<Modal, 'isOpen'>) => void;
  closeModal: (modalId: string) => void;
  closeAllModals: () => void;
  
  // Acciones de notificaciones
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (notificationId: string) => void;
  clearNotifications: () => void;
  
  // Acciones de configuración
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: 'es' | 'en') => void;
  toggleSidebar: () => void;
  
  // Acciones de carga
  setLoading: (loading: boolean, message?: string) => void;
  
  // Reset
  reset: () => void;
}

const initialState = {
  panels: {
    ifcInput: false,
    cadasterInput: false,
    configuration: false,
    drawing: false,
    map: false,
    calculations: false,
    export: false,
  },
  modals: [],
  activeModalId: null,
  notifications: [],
  theme: 'light' as const,
  language: 'es' as const,
  sidebarCollapsed: false,
  isLoading: false,
  loadingMessage: '',
};

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
      
        // Acciones de paneles
        togglePanel: (panelName) => set((state) => ({
          panels: {
            ...state.panels,
            [panelName]: !state.panels[panelName],
          },
        })),
        
        openPanel: (panelName) => set((state) => ({
          panels: {
            ...state.panels,
            [panelName]: true,
          },
        })),
        
        closePanel: (panelName) => set((state) => ({
          panels: {
            ...state.panels,
            [panelName]: false,
          },
        })),
        
        closeAllPanels: () => set((state) => ({
          panels: {
            ifcInput: false,
            cadasterInput: false,
            configuration: false,
            drawing: false,
            map: false,
            calculations: false,
            export: false,
          },
        })),
        
        // Acciones de modales
        openModal: (modal) => set((state) => {
          const newModal: Modal = {
            ...modal,
            isOpen: true,
          };
          return {
            modals: [...state.modals, newModal],
            activeModalId: newModal.id,
          };
        }),
        
        closeModal: (modalId) => set((state) => ({
          modals: state.modals.map(m => 
            m.id === modalId ? { ...m, isOpen: false } : m
          ),
          activeModalId: state.activeModalId === modalId ? null : state.activeModalId,
        })),
        
        closeAllModals: () => set({
          modals: [],
          activeModalId: null,
        }),
        
        // Acciones de notificaciones
        addNotification: (notification) => set((state) => {
          const newNotification: Notification = {
            ...notification,
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
          };
          return {
            notifications: [...state.notifications, newNotification],
          };
        }),
        
        removeNotification: (notificationId) => set((state) => ({
          notifications: state.notifications.filter(n => n.id !== notificationId),
        })),
        
        clearNotifications: () => set({ notifications: [] }),
        
        // Acciones de configuración
        setTheme: (theme) => set({ theme }),
        setLanguage: (language) => set({ language }),
        toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
        
        // Acciones de carga
        setLoading: (loading, message = '') => set({ isLoading: loading, loadingMessage: message }),
        
        // Reset
        reset: () => set(initialState),
      }),
      createPersistMiddleware('ui-store', { version: PERSIST_VERSION })
    ),
    {
      name: 'ui-store',
    }
  )
);
